import { z } from 'zod'

/**
 * A saved command (PROMPT.md §5.6). `{{name}}` placeholders are filled in through a dialog
 * before execution, and every substituted value is shell-quoted on the way in.
 */
export const snippetScopeSchema = z.enum(['global', 'servers'])
export type SnippetScope = z.infer<typeof snippetScopeSchema>

export const snippetSchema = z.object({
  id: z.string(),
  name: z.string(),
  command: z.string(),
  description: z.string(),
  /** Wraps the command in sudo according to the server's sudo mode (§5.3). */
  requiresSudo: z.boolean(),
  /** Inserted into the terminal but not submitted, so it can be read before it runs. */
  confirmBeforeRun: z.boolean(),
  scope: snippetScopeSchema,
  /** Empty when the scope is `global`. */
  serverIds: z.array(z.string()),
  /** Placeholder names found in the command, in order of first appearance. */
  placeholders: z.array(z.string()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type Snippet = z.infer<typeof snippetSchema>

const snippetFields = {
  name: z
    .string()
    .trim()
    .min(1, 'Name darf nicht leer sein.')
    .max(80, 'Name darf höchstens 80 Zeichen lang sein.'),
  command: z
    .string()
    .trim()
    .min(1, 'Das Kommando darf nicht leer sein.')
    .max(4000, 'Das Kommando darf höchstens 4000 Zeichen lang sein.'),
  description: z.string().max(500, 'Die Beschreibung darf höchstens 500 Zeichen lang sein.'),
  requiresSudo: z.boolean(),
  confirmBeforeRun: z.boolean(),
  scope: snippetScopeSchema,
  serverIds: z.array(z.string()),
}

export const createSnippetSchema = z
  .object({
    ...snippetFields,
    description: snippetFields.description.default(''),
    requiresSudo: snippetFields.requiresSudo.default(false),
    confirmBeforeRun: snippetFields.confirmBeforeRun.default(true),
    scope: snippetFields.scope.default('global'),
    serverIds: snippetFields.serverIds.default([]),
  })
  .refine((input) => input.scope === 'global' || input.serverIds.length > 0, {
    message: 'Wähle mindestens einen Server aus, oder stelle den Bereich auf "global".',
    path: ['serverIds'],
  })
export type CreateSnippetInput = z.infer<typeof createSnippetSchema>

export const updateSnippetSchema = z
  .object(snippetFields)
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Es wurde kein Feld zum Ändern übergeben.',
  })
export type UpdateSnippetInput = z.infer<typeof updateSnippetSchema>

export const snippetIdParamsSchema = z.object({ id: z.string().min(1) })

/** Running a snippet across several servers at once (§5.6). */
export const runSnippetSchema = z.object({
  serverIds: z.array(z.string()).min(1, 'Wähle mindestens einen Server aus.'),
  /** Values for the `{{name}}` placeholders; quoted before substitution. */
  values: z.record(z.string(), z.string()).default({}),
  /** Answer for `sudo_mode = 'ask'`, kept only for this request. */
  sudoPassword: z.string().optional(),
})
export type RunSnippetInput = z.infer<typeof runSnippetSchema>

export const snippetRunResultSchema = z.object({
  serverId: z.string(),
  serverName: z.string(),
  status: z.enum(['ok', 'failed', 'error']),
  /** Null when the command never ran or was killed by a signal. */
  exitCode: z.number().int().nullable(),
  stdout: z.string(),
  stderr: z.string(),
  durationMs: z.number().int().nonnegative(),
  truncated: z.boolean(),
  timedOut: z.boolean(),
  /** Set when the command could not be started at all. */
  error: z.string().nullable(),
})
export type SnippetRunResult = z.infer<typeof snippetRunResultSchema>

export const snippetRunResponseSchema = z.object({
  results: z.array(snippetRunResultSchema),
})
